import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ChannelLogo} from '../../channels/ChannelLogo';
import {ArrowRight, ChartBar, Trash} from '../../icons';
import {evidenceKindOf, isBudgetResolved} from '../budgetMath';
import {choosesGoogleConversion, needsConversion} from '../conversionSuggestion';
import {paceNoticeOf} from '../pace';
import {amountFor, BudgetAmount, Calculating, currencyFor, formatMoney} from './BudgetAmount';
import {ConversionPicker} from './ConversionPicker';
import {GoogleConversionPicker} from './GoogleConversionPicker';
import {PaceNotice} from './PaceNotice';

/**
 * El presupuesto de un cliente, con la evidencia de dónde salió.
 *
 * Con una sola cuenta se edita un monto; con varias, cada plataforma tiene el suyo y el total es la
 * suma. Es cómo las agencias reparten de verdad su inversión, y un monto único para un cliente
 * multi-plataforma obligaría a inventar una distribución al configurarlo.
 *
 * La suma es POR MONEDA: un cliente con una cuenta en pesos y otra en dólares no tiene un total.
 *
 * El desglose se abre solo cuando hay algo que ver cuenta por cuenta (UD-22, fede 27/10): con más
 * de una moneda —«ARS 6.400.000 + USD 1.400» no dice qué monto va a qué cuenta— o con un aviso de
 * ritmo, que es una decisión por cuenta. Los demás clientes quedan cerrados, con su total, para que
 * una cartera de 70 no se alargue sin motivo.
 */
export function BudgetRow({group, budgets, state, dispatch, preparing = true, canEditCurrency = false,
                           onDismiss, conversionEvents = null, conversionsLoading = false,
                           onRetryConversions = null, googleConversions = null}) {
    const {t, i18n} = useTranslation();
    const isMulti = group.accounts.length > 1;
    // `null` = el usuario todavía no abrió ni cerró el desglose: decide la fila.
    const [userExpanded, setUserExpanded] = useState(null);

    // Sólo las monedas que de verdad tienen un monto: una cuyas cuentas no tienen ninguno no tiene
    // total, y mostrarlo como «USD 0» convierte un dato ausente en un número que el usuario aprueba.
    const totals = totalsByCurrencyOf(group, budgets, state).filter(each => each.amount !== null);
    // Sólo monedas declaradas: una cuenta sin moneda no es «otra moneda», y la nota diría «ARS y —»
    // (review de UD-22).
    const mixedCurrencies = isMulti && totals.filter(each => each.currency).length > 1;
    const hasPaceNotice = isMulti && group.accounts.some(entry => paceNoticeOf(budgets?.[String(entry.pk)],
                                                                               state.budgetEdits[entry.pk]));
    // Una vez abierta por la fila, queda abierta: editar a mano el monto de la cuenta del aviso lo
    // apaga, y el desglose no se puede cerrar debajo del campo que el usuario está tocando. Es el
    // patrón de React para guardar algo del render anterior (setState durante el render).
    const [openedItself, setOpenedItself] = useState(false);
    if ((mixedCurrencies || hasPaceNotice) && !openedItself) setOpenedItself(true);
    // Con dos monedas no hay total que mostrar cerrado, así que tampoco se puede cerrar.
    const expanded = mixedCurrencies || (userExpanded ?? openedItself);
    const toggle = () => setUserExpanded(!expanded);
    // El total de un cliente con varias cuentas es una SUMA, y mientras alguna todavía espera el
    // catálogo esa suma crece sola: el de Ford pasó de COP 4.200.000 a 8.700.000 con Leo leyéndolo
    // (card C9zl8x1e). Hasta que todas estén resueltas no se muestra un número que va a cambiar.
    const calculating = isMulti && preparing && group.accounts.some(entry => isStillCalculating(entry, budgets, state));
    // La pastilla de gasto también es una suma de las cuentas del cliente, y también crece cuando
    // llegan las que faltan: mientras el total dice «Calculando…», no se dibuja (review C-tunel-01).
    const spent = calculating ? [] : observedSpendByCurrencyOf(group, budgets);
    const anyEstimated = !calculating
        && group.accounts.some(entry => evidenceKindOf(budgets?.[String(entry.pk)]) === 'estimated');

    return (
        <div className={`ob-budget${expanded ? ' -expanded' : ''}`}>
            <div className="ob-budget__row">
                <div className="ob-budget__client">
                    <span className="ob-budget__name">{group.name}</span>
                    <span className="ob-budget__detail">
                        {group.accounts.map(entry => (
                            <ChannelLogo key={entry.pk} channel={entry.account.channel}
                                         hspace={0} vspace={0} marginLeft={0} padding={0}/>
                        ))}
                        {isMulti
                            ? t('onboarding.budgets.accounts_per_platform', {count: group.accounts.length})
                            : t('onboarding.budgets.account_count', {count: group.accounts.length})}
                    </span>
                </div>

                {mixedCurrencies ? (
                    /* Sin total ni pastilla sumada: cada subfila tiene su gasto y su monto, en su
                       moneda. Esto dice por qué la fila viene abierta (UD-22). */
                    <span className="ob-budget__mixed">
                        {t('onboarding.budgets.mixed_currencies',
                           {currencies: listOf(totals.map(each => each.currency || '—'), i18n.language),
                            interpolation: {escapeValue: false}})}
                    </span>
                ) : isMulti ? (
                    <>
                        {/* La referencia del gasto, también para el cliente con presupuesto por
                            plataforma: Ford (3 cuentas) era la única fila sin «gastó…», justo la
                            que tiene un total que nadie calculó a mano (card C9zl8x1e). Es la suma
                            de lo que gastaron sus cuentas, por moneda, como el total de al lado. */}
                        {spent.length ? (
                            <span className="ob-budget__evidence">
                                <ChartBar size={13}/>
                                {t('onboarding.budgets.evidence_observed', {
                                    amount: spent.map(each => formatMoney(each.amount, each.currency, i18n.language))
                                                 .join(' + '),
                                    interpolation: {escapeValue: false}})}
                            </span>
                        ) : anyEstimated ? (
                            <span className="ob-budget__evidence">
                                <ChartBar size={13}/>
                                {t('onboarding.budgets.evidence_estimated')}
                            </span>
                        ) : (
                            /* Relleno de la pista de evidencia. Sin clase: `.ob-budget__evidence`
                               pinta un chip (fondo, padding y borde redondeado) y como item de grid
                               se estiraba a toda la pista — una barra gris en cada fila multi-cuenta. */
                            <span/>
                        )}
                        <ArrowRight size={14} className="ob-budget__arrow"/>
                        <div className="ob-budget__amount">
                            {/* Con más de una moneda no hay un total: sumar ARS con USD produce un
                                número que no existe, y este es el paso donde el usuario aprueba
                                montos mirando. Se muestra un total POR moneda. */}
                            {/* El total abre el desglose en vez de no hacer nada. En una fila de
                                varias cuentas el monto NO se edita acá —cada plataforma tiene el
                                suyo— pero el usuario igual le hace clic: es el número que está
                                mirando. Sin esto el clic caía en el vacío y la fila parecía rota. */}
                            {calculating ? (
                                <Calculating/>
                            ) : totals.length ? (
                                <button type="button" aria-expanded={expanded}
                                        className={`ob-budget__total${totals.length > 1 ? ' -multi' : ''}`}
                                        onClick={toggle}>
                                    {totals.map(each => formatMoney(each.amount, each.currency, i18n.language))
                                           .join(' + ')}
                                </button>
                            ) : (
                                /* Ninguna de sus cuentas tiene monto: la misma respuesta que da la
                                   fila de UNA cuenta en la misma situación. Antes las dos formas de
                                   esta pantalla contestaban distinto sobre el mismo hecho. */
                                <span className="ob-budget__empty">{t('onboarding.budgets.no_budget')}</span>
                            )}
                            <button type="button" className="ob-link" aria-expanded={expanded} onClick={toggle}>
                                {expanded ? t('onboarding.budgets.hide_split') : t('onboarding.budgets.show_split')}
                            </button>
                        </div>
                    </>
                ) : (
                    <SingleAccountBudget entry={group.accounts[0]} budgets={budgets} state={state}
                                         dispatch={dispatch} preparing={preparing}
                                         canEditCurrency={canEditCurrency}/>
                )}

                {/* Descartar el cliente entero desde acá. El paso 4 es donde el usuario ve por
                    primera vez cuánta plata monitorea cada línea, y ahí es donde se da cuenta de
                    que una no va — obligarlo a volver al paso 3 para eso le cuesta el trabajo que
                    hizo en éste. */}
                {onDismiss ? (
                    /* «No crear», no «Quitar» (H-03, recorrido con Pablo del 13/10): Pablo quería
                       arrancar sin presupuesto, apretó «Quitar» pegado al campo del monto y sacó al
                       cliente entero. Dos acciones vecinas sobre objetos distintos con un rótulo que
                       servía para los dos. El rótulo dice ahora el objeto, el monto tiene su propio
                       «Sin presupuesto» adentro del editor, y la celda va separada del monto por una
                       línea (ver el SCSS). El cartel lo dice entero; el `aria-label` agrega el
                       nombre del cliente, que el botón no muestra. */
                    <span className="ob-budget__act-wrap">
                        <button type="button" className="ob-budget__act -remove" onClick={onDismiss}
                                aria-label={t('onboarding.budgets.dismiss_client',
                                              {client: group.name, interpolation: {escapeValue: false}})}>
                            <Trash size={14}/> {t('onboarding.budgets.dismiss_short')}
                        </button>
                        <span className="ob-tip" aria-hidden="true">{t('onboarding.budgets.dismiss_tooltip')}</span>
                    </span>
                ) : <span/>}
            </div>

            {/* Fuera de la grilla de la fila y no como una columna más: sólo las cuentas de Meta la
                tienen, y una pista vacía en todas las demás filas correría los montos. Con el
                desglose abierto se dibuja debajo de la subfila de SU cuenta: arriba del desglose se
                leía como un dato del cliente y no de la cuenta. */}
            {/* El aviso de ritmo va primero: habla del monto, que es lo que está arriba. */}
            {!(isMulti && expanded) && (
                <PaceNotice entries={group.accounts} budgets={budgets} budgetEdits={state.budgetEdits}
                            dispatch={dispatch} showAccountName={isMulti}/>
            )}
            {!(isMulti && expanded) && (
                <ConversionPicker group={group} conversionEvents={conversionEvents}
                                  conversionsLoading={conversionsLoading} onRetry={onRetryConversions}
                                  conversionEdits={state.conversionEdits} dispatch={dispatch}/>
            )}
            {/* Las cuentas de Google (C-01), sólo con el backend que acepta su `selected_actions`:
                `googleConversions` viene en null con el backend viejo, y la fila queda como antes. */}
            {googleConversions && !(isMulti && expanded) && (
                <GoogleConversionPicker group={group} conversionEdits={state.conversionEdits} dispatch={dispatch}
                                        accountActions={googleConversions.accountActions}
                                        loadAccountActions={googleConversions.loadAccountActions}/>
            )}

            {isMulti && expanded && (
                <div className="ob-budget__split">
                    {group.accounts.map(entry => (
                        <React.Fragment key={entry.pk}>
                        <div className="ob-budget__row -sub">
                            {/* Clase propia y no `__client`: aquella apila en columna (nombre +
                                detalle de la fila cabecera) y acá el logo va AL LADO del nombre. */}
                            <div className="ob-budget__subclient">
                                <ChannelLogo channel={entry.account.channel} hspace={0} vspace={0}
                                             marginLeft={0} padding={0}/>
                                <span>{entry.account.name || entry.account.account_id}</span>
                            </div>
                            <SingleAccountBudget entry={entry} budgets={budgets} state={state} dispatch={dispatch}
                                                 preparing={preparing} canEditCurrency={canEditCurrency}/>
                            {/* La columna de acciones existe en toda la grilla: sin este relleno
                                la subfila corre el monto una pista a la derecha. */}
                            <span/>
                        </div>
                        <PaceNotice entries={[entry]} budgets={budgets} budgetEdits={state.budgetEdits}
                                    dispatch={dispatch}/>
                        {/* La subfila ya nombra la cuenta: la línea no la repite. */}
                        {needsConversion(entry.account) && (
                            <div className="ob-conversion__sub">
                                <ConversionPicker group={{...group, accounts: [entry]}} showAccountName={false}
                                                  conversionEvents={conversionEvents}
                                                  conversionsLoading={conversionsLoading}
                                                  onRetry={onRetryConversions}
                                                  conversionEdits={state.conversionEdits} dispatch={dispatch}/>
                            </div>
                        )}
                        {googleConversions && choosesGoogleConversion(entry.account) && (
                            <div className="ob-conversion__sub">
                                <GoogleConversionPicker group={{...group, accounts: [entry]}} showAccountName={false}
                                                        conversionEdits={state.conversionEdits} dispatch={dispatch}
                                                        accountActions={googleConversions.accountActions}
                                                        loadAccountActions={googleConversions.loadAccountActions}/>
                            </div>
                        )}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
}

/** La pieza compartida con el modal del Panel (`BudgetAmount`), conectada al reducer del túnel. */
function SingleAccountBudget({entry, budgets, state, dispatch, preparing = true, canEditCurrency = false}) {
    return (
        <BudgetAmount suggestion={budgets?.[String(entry.pk)]} edit={state.budgetEdits[entry.pk]}
                      onEdit={(amount, currency) => dispatch({type: 'EDIT_BUDGET', pk: entry.pk,
                                                              amount, currency})}
                      preparing={preparing} canEditCurrency={canEditCurrency} offerNoBudget/>
    );
}

/**
 * ¿El monto de esta cuenta todavía puede aparecer o cambiar solo?
 *
 * Es la misma pregunta que el contador «N de M cuentas listas» (`isBudgetResolved`), vista desde la
 * fila, y la misma que hace `BudgetAmount` para la fila de una cuenta. Lo que el usuario ya editó no
 * cambia más: su monto gana sobre lo que llegue.
 */
function isStillCalculating(entry, budgets, state) {
    if (state.budgetEdits[entry.pk]) return false;
    return !isBudgetResolved(budgets?.[String(entry.pk)]);
}

/**
 * Lo que el cliente gastó el mes pasado, sumando sus cuentas y POR MONEDA — el mismo criterio que
 * el total del presupuesto: sumar ARS con USD produce un número que no existe.
 *
 * Sólo cuentan las cuentas con gasto OBSERVADO: las demás no gastaron (o no lo sabemos), y el
 * número es un hecho del pasado, no una estimación.
 */
function observedSpendByCurrencyOf(group, budgets) {
    const byCurrency = new Map();
    group.accounts.forEach(entry => {
        const suggestion = budgets?.[String(entry.pk)];
        if (evidenceKindOf(suggestion) !== 'observed') return;
        const amount = Number(suggestion.amount);
        if (!Number.isFinite(amount)) return;
        const key = suggestion.currency || '';
        const previous = byCurrency.get(key)?.amount || 0;
        byCurrency.set(key, {currency: suggestion.currency || null, amount: previous + amount});
    });
    return Array.from(byCurrency.values());
}

/** «ARS y USD», «ARS, USD y COP»: la lista en el idioma del usuario. */
function listOf(items, locale) {
    try {
        return new Intl.ListFormat(locale || 'es', {style: 'long', type: 'conjunction'}).format(items);
    } catch (e) {
        return items.join(', ');
    }
}

function amountOf(pk, budgets, state) {
    return amountFor(budgets?.[String(pk)], state.budgetEdits[pk]);
}

function currencyOf(pk, budgets, state) {
    return currencyFor(budgets?.[String(pk)], state.budgetEdits[pk]);
}

/**
 * El total del cliente, uno por cada moneda que aparezca.
 *
 * Antes se sumaba todo y se rotulaba con la primera moneda del grupo: un cliente con una cuenta en
 * ARS y otra en USD veía un número que no existe, justo en el paso donde aprueba montos. El payload
 * ya viaja bien —cada ítem lleva la suya—, así que esto es sólo lo que se muestra.
 *
 * Con una sola moneda devuelve una sola entrada, que es el caso normal y se ve igual que siempre.
 */
function totalsByCurrencyOf(group, budgets, state) {
    const byCurrency = new Map();
    group.accounts.forEach(entry => {
        const currency = currencyOf(entry.pk, budgets, state);
        // La cuenta sin moneda declarada no se mezcla con ninguna: el backend la devuelve nula
        // justamente para no inventarle una.
        const key = currency || '';
        const running = byCurrency.get(key);
        const amount = amountOf(entry.pk, budgets, state);
        // `null` no es 0. Una moneda cuyas cuentas no tienen ningún monto no tiene total, y sumarla
        // como cero producía «USD 0» sobre cuatro cuentas que dicen «La línea nace sin presupuesto»
        // — un número que no existe, en el lugar donde el usuario aprueba los montos.
        const previous = running ? running.amount : null;
        byCurrency.set(key, {
            currency: currency || null,
            amount: (amount === null || amount === undefined)
                ? previous
                : (previous === null ? 0 : previous) + amount,
        });
    });
    return Array.from(byCurrency.values());
}
